<template>
  <div>
    <el-card>
      <card-header :title="isEdit ? $t('ratesUpdate') : $t('ratesCreate')"/>
      <form>
        <lang-tabs
          :inputName="$t('ratesName')"
          :inputKey="'name'"
          :inputValue="rate.name"
          @langData="updateLangData"
          :langErrors="{name: error.name ? error.name : error['name']}"
        />
        <br>
        <SelectField
          :label="$t('ratesSelectProvider')"
          :disabled="userRole === 'admin' && rate.rate_type === 'supplier' ? false : true"
          optionLabel="legal_name"
          :data="rate.rate_type === 'distributor' ? distributors : suppliers"
          :errors="error.provider_id ? error.provider_id[0] : ''"
          :value="rate.provider_id"
          v-model="rate.provider_id"
        />

        <SelectField
          v-if="isSupplierRate"
          :label="$t('ratesSelectDistributor')"
          optionLabel="display_name"
          :disabled="!isSupplierRate"
          :data="distributors"
          :errors="error.distributor_id ? error.distributor_id[0] : ''"
          :value="rate.distributor_id"
          v-model="rate.distributor_id"
        />

        <SelectField
          :label="$t('ratesSelectContractTerm')"
          optionLabel="name"
          :data="types.contractTermTypes"
          :errors="error.contract_term ? error.contract_term[0] : ''"
          :value="rate.contract_term"
          v-model="rate.contract_term"
        />

        <SelectField
          :label="$t('ratesSelectSeasonType')"
          optionLabel="name"
          :data="types.seasonTypes"
          :errors="error.season_type ? error.season_type[0] : ''"
          :value="rate.season_type"
          v-model="rate.season_type"
        />

        <SelectField
          :label="$t('ratesSelectCustomerType')"
          optionLabel="description"
          :data="customerTypes"
          :errors="error.customer_type_id ? error.customer_type_id[0] : ''"
          :value="rate.customer_type_id"
          v-model="rate.customer_type_id"
        />

        <SelectField
          v-if="showPowerMeasurementAndConsumer"
          :label="$t('ratesSelectPowerMeasurement')"
          optionLabel="name"
          :data="types.powerMeasurementTypes"
          :errors="error.power_measurement ? error.power_measurement[0] : ''"
          :value="rate.power_measurement"
          v-model="rate.power_measurement"
        />

        <SelectField
          v-if="showMeterType"
          :label="$t('ratesSelectMeterType')"
          optionLabel="name"
          :data="types.meterTypes"
          :errors="error.meter_type ? error.meter_type[0] : ''"
          :value="rate.meter_type"
          v-model="rate.meter_type"
        />

        <div style="display: flex; flex-direction: row" v-if="showPowerMeasurementAndConsumer">
          <el-radio v-model="rate.small_consumer" :label="true">{{$t('ratesSmallConsumerLabel')}}</el-radio>
          <el-radio v-model="rate.small_consumer" :label="false">{{$t('ratesBigConsumerLabel')}}</el-radio>
        </div>
        
        <br>
        <InputField
          :label="$t('ratesActiveEnergyHT')"
          typeField="Number"
          :errors="error.active_energy_vt ? error.active_energy_vt[0] : ''"
          v-model="rate.active_energy_vt"
        />

        <InputField
          v-if="!singleRate"
          :label="$t('ratesActiveEnergyLT')"
          typeField="Number"
          :errors="error.active_energy_mt ? error.active_energy_mt[0] : ''"
          v-model="rate.active_energy_mt"
        />

        <InputField
          :label="$t('ratesReactiveEnergyHT')"
          typeField="Number"
          :errors="error.reactive_energy_vt ? error.reactive_energy_vt[0] : ''"
          v-model="rate.reactive_energy_vt"
        />

        <InputField
          v-if="!singleRate"
          :label="$t('ratesReactiveEnergyLT')"
          typeField="Number"
          :errors="error.reactive_energy_mt ? error.reactive_energy_mt[0] : ''"
          v-model="rate.reactive_energy_mt"
        />

        <InputField
          :label="$t('ratesBillingCapacityHT')"
          typeField="Number"
          :errors="error.billing_capacity_vt ? error.billing_capacity_vt[0] : ''"
          v-model="rate.billing_capacity_vt"
        />

        <InputField
          v-if="!singleRate"
          :label="$t('ratesBillingCapacityLT')"
          typeField="Number"
          :errors="error.billing_capacity_mt ? error.billing_capacity_mt[0] : ''"
          v-model="rate.billing_capacity_mt"
        />

        <InputField
          :label="$t('ratesLossHT')"
          typeField="Number"
          :errors="error.loss_vt ? error.loss_vt[0] : ''"
          v-model="rate.loss_vt"
        />

        <InputField
          v-if="!singleRate"
          :label="$t('ratesLossLT')"
          typeField="Number"
          :errors="error.loss_mt ? error.loss_mt[0] : ''"
          v-model="rate.loss_mt"
        />

        <InputField
          :label="$t('ratesNetworkCapacity')"
          typeField="Number"
          :errors="error.network_capacity ? error.network_capacity[0] : ''"
          v-model="rate.network_capacity"
        />

        <InputField
          :label="$t('ratesFixedNetworkCapacity')"
          typeField="Number"
          :errors="error.fixed_network_capacity ? error.fixed_network_capacity[0] : ''"
          v-model="rate.fixed_network_capacity"
        />

        <InputField
          :label="$t('ratesEnergyEfficiency')"
          typeField="Number"
          :errors="error.energy_efficiency ? error.energy_efficiency[0] : ''"
          v-model="rate.energy_efficiency"
        />

        <InputField
          :label="$t('ratesMeteringPoint')"
          typeField="Number"
          :errors="error.metering_point ? error.metering_point[0] : ''"
          v-model="rate.metering_point"
        />

        <InputField
          :label="$t('ratesOIE')"
          typeField="Number"
          :errors="error.OIE ? error.OIE[0] : ''"
          v-model="rate.OIE"
        />

        <InputField
          :label="$t('ratesOperator')"
          typeField="Number"
          :errors="error.operator ? error.operator[0] : ''"
          v-model="rate.operator"
        />

        <InputField
          :label="$t('ratesRenewable')"
          typeField="Number"
          :errors="error.renewable ? error.renewable[0] : ''"
          v-model="rate.renewable"
        />

        <br>

        <div class="form-field">
          <span>{{$t('ratesValidityPeriod')}}</span>
          <el-date-picker
            v-model="rate.valid"
            type="datetimerange"
            format="yyyy/MM/dd  HH:mm:ss"
            range-separator="To"
            value-format="yyyy-MM-dd HH:mm:ss"
            start-placeholder="Start Date"
            end-placeholder="End Date"
            :default-time="['12:00:00']"
            :default-date="['2018-01-01']"
            :readonly="rate.confirmed_at ? true : false"
          ></el-date-picker>
          <br>
          <small>
            {{`${error.valid_from ? error.valid_from[0] : ''}`}}
            <br>
            {{`${error.valid_from ? error.valid_from[0] : ''}`}}
          </small>
        </div>
        <br>
        <lang-tabs
          :inputName="$t('ratesSeasonDescription')"
          :inputKey="'season_description'"
          :inputValue="rate.season_description"
          @langData="updateLangData"
          :langErrors="{season_description: error.season_description ? error.season_description : error['season_description.en']}"
        />
        <div v-if="userRole === 'admin'" class="form-field">
          <label></label>
          <el-checkbox border v-model="autoConfirm">{{$t('ratesConfirm')}}</el-checkbox>
        </div>
        <div v-if="isEdit" class="lang-tabs">
          <table>
            <tr>
              <td class="res-name">{{ $t('ratesCreatedBy') }}</td>
              <td
                class="res-value-clickable"
                @click="$router.push({name: 'SingleUser', params: {id: rate.created_by.id}})"
              >{{rate.created_by ? rate.created_by.name : '/'}}</td>
            </tr>
            <tr>
              <td class="res-name">{{ $t('ratesCreatedAt') }}</td>
              <td class>{{rate.created_at ? rate.created_at.date.slice(0,19) : '/'}}</td>
            </tr>
            <tr>
              <td class="res-name">{{ $t('ratesUpdatedBy') }}</td>
              <td
                class="res-value-clickable"
                @click="rate.updated_by ? $router.push({name: 'SingleUser', params: {id: rate.created_by.id}}) : ''"
              >{{rate.updated_by ? rate.updated_by.name : '/'}}</td>
            </tr>
            <tr>
              <td class="res-name">{{ $t('ratesUpdatedAt') }}</td>
              <td class>{{rate.updated_at ? rate.updated_at.date.slice(0,19) : '/'}}</td>
            </tr>
            <tr>
              <td class="res-name">{{ $t('ratesConfirmedBy') }}</td>
              <td
                class="res-value-clickable"
                @click="$router.push({name: 'SingleUser', params: {id: rate.confirmed_by.id}})"
              >{{rate.confirmed_by ? rate.confirmed_by.name : '/'}}</td>
            </tr>
            <tr>
              <td class="res-name">{{ $t('ratesConfirmedAt') }}</td>
              <td class>{{rate.confirmed_at ? rate.confirmed_at.date.slice(0,19) : '/'}}</td>
            </tr>
            <tr>
              <td class="res-name">{{ $t('ratesPublishedAt') }}</td>
              <td class>{{rate.published_at ? rate.published_at.date.slice(0,19) : '/'}}</td>
            </tr>
          </table>
        </div>
      </form>
    </el-card>
    <shared-buttons
      discardPath="Rates"
      @confirm-event-fired="saveRate"
      :isEdit="isEdit ? true : false"
    />
  </div>
</template>

<script>
import model from "@/model/index";
import { mapGetters } from "vuex";
import api from "@/api/api";
import CardHeader from "../LayoutPart/CardHeader";
import LangTabs from "../LangTabs/LangTabs";
import InputField from "../Inputs/InputField";
import SelectField from "../Inputs/SelectField";
import Types from "@/model/dropdownTypes";

export default {
  props: {
    rate: {},
    isEdit: {
      type: Boolean
    }
  },
  components: {
    CardHeader,
    InputField,
    LangTabs,
    SelectField
  },
  computed: {
    ...mapGetters({
      suppliers: "getSuppliers",
      distributors: "getDistributors",
      customerTypes: "getCustomerTypes",
      currentUser: "getCurrentUser",
      getAutoConfirm: "getAutoConfirmation"
    }),
    descriptions: () => {
      return model.getFields("rates");
    },
    getAutoConfirm() {
      return localStorage.getItem("auto");
    },
    userRole() {
      return localStorage.getItem("user");
    },
    showMeterType() {
      let { customer_type_id, power_measurement } = this.rate;
      if (customer_type_id === 1) return true;
      else if (customer_type_id === 5 && power_measurement === 'without') return true;
      else return false;
    },
    showPowerMeasurementAndConsumer() {
      let { customer_type_id } = this.rate
      return customer_type_id === 5 ?  true : false;
    },
    singleRate() {
      let { meter_type } = this.rate;
      return meter_type === 'single_rate_meter' ? true : false;
    }
  },
  data() {
    return {
      activeNames: "0",
      autoConfirm: false,
      error: {},
      isSupplierRate: true,
      types: Types
    };
  },
  async created() {
    this.$route.name === "SupplierRateEdit" ||
    this.$route.name === "SupplierRateCreate"
      ? (this.isSupplierRate = true)
      : (this.isSupplierRate = false);
    this.getAutoConfirm === "true" || this.getAutoConfirm === true
      ? (this.autoConfirm = true)
      : (this.autoConfirm = false);
    !this.isSupplierRate
      ? (this.rate.provider_id = this.distributors[0].id)
      : "";
    this.$store.dispatch("fetchSelf");
    if (this.isEdit) {
      this.rate.provider_id = this.rate.provider.id;
      this.rate.distributor
        ? (this.rate.distributor_id = this.rate.distributor.id)
        : (this.isSupplierRate = false);
      this.rate.customer_type_id = this.rate.customer_type.id;
      this.rate.valid_from && this.rate.valid_till
        ? (this.rate.valid = [
            this.rate.valid_from.date,
            this.rate.valid_till.date
          ])
        : "";
    }
    if (this.userRole === "provider") {
      this.rate.provider_id = this.currentUser.provider.id;
    }
  },
  methods: {
    saveRate() {
      let url, id;
      if (this.isEdit) {
        url = "updateRate";
      } else {
        url = "addRate";
      }

      !this.rate.meter_type ? this.rate.meter_type = 'double_rate_meter' : null;

      this.$store.dispatch(url, this.rate).then(res => {
        if (res.status === "success") {
          if (this.autoConfirm === "true" || this.autoConfirm === true) {
            this.confirmSingleRate(res.rate.id);
          } else {
            this.$router.go(-1);
          }
        } else {
          this.error = res;
        }
      });
    },
    confirmSingleRate(rate) {
      this.$store.dispatch("confirmSingleRate", rate).then(res => {
        this.$router.go(-1);
      });
    },
    updateLangData(updatedData) {
      this.rate[Object.keys(updatedData)[0]] =
        updatedData[Object.keys(updatedData)[0]];
    },
    handleChange(val) {}
  }
};
</script>

<style scoped>
table {
  width: 100%;
}

table > tr > td {
  padding: 15px !important;
  border-bottom: 1px solid #e6e6e6;
}

table > tr:last-child > td {
  border: none;
}

table > tr > td:nth-child(2) {
  position: relative;
  margin-left: -3px;
  display: block;
  margin-top: 1px;
}
.res-name {
  width: 300px;
  color: #303133;
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 10px;
  object-fit: contain;
}
.res-value-clickable {
  color: #409eff !important;
  cursor: pointer;
}
small {
  color: red;
}

.el-collapse-item__header {
  font-size: 14px !important;
}

el-collapse-item__content {
  margin-left: 10px !important;
}

.block {
  display: block;
}

.right {
  float: right;
}

.button {
  margin-left: 10px;
}

.questionMark {
  text-align: center;
  border-radius: 50%;
  background-color: #469063;
  color: whitesmoke;
  font-family: "Arial", sans-serif;
  font-size: 14px;
  padding-left: 3px;
  padding-right: 3px;
}

.error-message {
  color: red;
  display: block;
}

/* .el-card__body form > div:not(.lang-tabs) {
        padding-left: 20px;
        display: inline-block;
        width: 48%;
        vertical-align: top;
    } */

.el-card__body form > div.lang-tabs + div {
  display: block;
  width: 100%;
}
.el-card__body form > div.form-field {
  margin-top: 10px;
  margin-bottom: 10px;
  display: block;
  width: 100%;
}

.el-card__body form > div.form-field > span {
  margin-bottom: 10px;
}

.el-card__body form > div > span {
  display: block;
}

.el-card__body form > div > input {
  width: 100%;
}

@media screen and (min-width: 1200px) {
  .el-card__body form > div:not(.lang-tabs) {
    display: inline-block;
    width: 20vw;
    vertical-align: top;
  }
  form {
    width: 60%;
  }
}
@media screen and (max-width: 601px) {
  .el-card__body form > div:not(.lang-tabs) {
    display: block;
    width: 20vw;
    vertical-align: top;
  }
  form {
    width: 100%;
  }
}

.el-radio {
  margin-bottom: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/Rates/RateForm.vue